package com.wintermar.inspection.feature.presentation.inspection.answer

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.wintermar.inspection.core.ui.component.connectivity_monitor.util.ConnectivityMonitorStatus
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.answer.CompleteAnswerModel
import com.wintermar.inspection.feature.domain.model.answer.SummaryAnswerModel
import com.wintermar.inspection.feature.domain.model.template.InspectionPageModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.presentation.inspection.answer.modal_state.ModalInCompleteState
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.graphql.type.InspectionStatus

class InspectionAnswerState(
    private val inspectionId: String,
    private val onUpdateProgressInspection: (
        vesselName: String,
        totalAnswer: Int,
        skipAutoClose: Boolean
    ) -> Unit,
    private val onCompleteInspection: () -> Unit,
    private val onSearchQuestion: () -> Unit,
    val onSaveAnswer: (
        answer: AnswerModel,
        answerType: InspectionAnswerType,
        includeUploadFile: Boolean
    ) -> Unit,
    val onReattachUploadCallback: () -> Unit
) : AnswerState(
    inspectionId = inspectionId,
    onSaveAnswer = onSaveAnswer,
    onReattachUploadCallback = onReattachUploadCallback
) {
    var connectivityMonitorStatus by mutableStateOf(ConnectivityMonitorStatus.CONNECTED)
    val isLoadingDetailTemplate: Boolean
        get() = isLoading
    var isLoadingCompleteInspection by mutableStateOf(false)
    var pageOrder by mutableStateOf(0)
    val page: InspectionPageModel
        get() = template.pages.getOrNull(pageOrder) ?: InspectionPageModel()

    val showPrevButton get() = pageOrder > 0
    val showNextButton get() = pageOrder < template.numberOfPages - 1
    val showCompleteButton get() = !showNextButton && pageOrder > 0
    var modalInComplete by mutableStateOf(ModalInCompleteState())
    var completeScreen by mutableStateOf(false)
    var completeAnswer by mutableStateOf(CompleteAnswerModel())
    val totalCount: Int
        get() = page.sections.sumOf { it.questions.size }
    val answeredCount: Int
        get() = page.sections.sumOf { section ->
            section.questions.count { q -> q.answer.isAnswered(q.answerType) }
        }

    private fun getTotalAnswer(): Int {
        return template.getTotalAnswer()
    }

    private fun getVesselName(): String {
        return template.pages
            .flatMap { it.sections }
            .flatMap { it.questions }
            .find {
                it.answerType === InspectionAnswerType.VESSEL_ID
            }?.answer?.answerSelection?.answerSelectionValue ?: ""
    }

    fun expandSection(sectionId: String, isExpand: Boolean) {
        template = template.copy(
            pages = template.pages.map { page ->
                page.copy(
                    sections = page.sections.map { section ->
                        if (section.id == sectionId) section.copy(isExpanded = isExpand)
                        else section
                    }
                )
            }
        )
    }

    fun onNextPage() {
        if (showNextButton) pageOrder++
    }

    fun onPrevPage() {
        if (showPrevButton) pageOrder--
    }

    fun onGoToSpecificPage(pageOrder: Int) {
        this.pageOrder = pageOrder
    }

    fun onComplete() {
        val resultEmptyRequiredQuestion: List<SummaryAnswerModel> =
            template.pages.flatMap { page ->
                page.sections.flatMap { section ->
                    section.questions
                        .filter {
                            it.answer.isEmptyAnswer(
                                answerType = it.answerType,
                                required = it.required
                            )
                        }
                        .map { question ->
                            SummaryAnswerModel(
                                pageTitle = page.title,
                                sectionTitle = section.title,
                                question = question
                            )
                        }
                }
            }

        val resultNeedDocument: List<SummaryAnswerModel> =
            template.getFlaggedAnswer { it.isEmptyFileAndNotes() || it.isEmptyFindingCategory() }
        if (resultNeedDocument.isNotEmpty() || resultEmptyRequiredQuestion.isNotEmpty()) {
            onShowModalInComplete(
                show = true,
                unAnswerList = resultEmptyRequiredQuestion,
                needDocumentList = resultNeedDocument
            )
        } else {
            saveProgress(true)
            completeAnswer = CompleteAnswerModel(
                inspectionId = inspectionId,
                vesselName = getVesselName(),
                totalAnswer = getTotalAnswer(),
                templateName = template.name,
                totalFiles = template.pages
                    .flatMap { it.sections }
                    .flatMap { it.questions }
                    .flatMap { it.answer.files }
                    .size,
                totalFindings = template.pages
                    .flatMap { it.sections }
                    .flatMap { it.questions }
                    .filter { it.answer.isRequiredFileOrNotes }
                    .size,
                totalQuestion = template.numberOfQuestions
            )
            onCompleteInspection()
        }
    }

    fun onSuccessCompleteInspection(status: InspectionStatus, lastUpdate: String) {
        completeAnswer = completeAnswer.copy(
            inspectionStatus = status,
            lastUpdated = lastUpdate
        )
        completeScreen = true
    }

    fun onShowModalInComplete(
        show: Boolean,
        unAnswerList: List<SummaryAnswerModel> = emptyList(),
        needDocumentList: List<SummaryAnswerModel> = emptyList()
    ) {
        modalInComplete = modalInComplete.copy(
            show = show,
            unAnswerList = unAnswerList,
            needDocumentList = needDocumentList
        )
    }

    fun onCompleteRemainingItem() {
        pageOrder = 0
    }

    fun saveProgress(skipAutoClose: Boolean = false) {
        val totalAnswer = getTotalAnswer()
        val vesselName = getVesselName()

        onUpdateProgressInspection(vesselName, totalAnswer, skipAutoClose)
    }

    fun onSearchClick() {
        onSearchQuestion()
    }

    fun onUpdateSearchData(
        inspectionTemplate: InspectionTemplateModel,
    ) {
        template = inspectionTemplate
        onReattachUploadCallbackListener()
    }

    fun onGoToSpecificContent(
        selectedPageId: String?,
        selectedSectionId: String?,
        selectedQuestionId: String?,
        onScrollSection: (position: Int, sectionId: String) -> Unit,
        onScrollQuestion: (questionId: String) -> Unit
    ) {
        if (selectedPageId.isNullOrBlank() && selectedSectionId.isNullOrBlank() && selectedQuestionId.isNullOrBlank()) return
        if (selectedPageId?.isNotBlank() == true) {
            val pagePosition = template.pages.indexOfFirst { it.id == selectedPageId }
            onGoToSpecificPage(pagePosition)
            return
        }

        if (selectedSectionId?.isNotBlank() == true) {
            val page = template.getPageBySection(selectedSectionId) ?: return
            val pagePosition = template.pages.indexOfFirst { it.id == page.id }
            onGoToSpecificPage(pagePosition)
            val scrollableSectionPosition =
                page.sections.indexOfFirst { it.id == selectedSectionId }
            onScrollSection(scrollableSectionPosition, selectedSectionId)
            return
        }

        val page = template.getPageByQuestion(selectedQuestionId ?: return) ?: return
        val pagePosition = template.pages.indexOfFirst { it.id == page.id }
        onGoToSpecificPage(pagePosition)
        val scrollableSectionPosition =
            page.sections.indexOfFirst { section -> section.questions.any { it.id == selectedQuestionId } }
        val section = page.sections[scrollableSectionPosition]
        val sectionId = section.id
        onScrollSection(scrollableSectionPosition, sectionId)
        onScrollQuestion(selectedQuestionId)
    }

    fun isAllFileUploaded(): Boolean {
        return template.isAllAvailableFilesCompleteUploaded()
    }
}
